import React, { useMemo } from 'react';
import { OrderInfo } from 'common/types';
import ContainerBox from 'components/ContainerBox';
import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import { GridSlotsComponent, useGridApiRef } from '@mui/x-data-grid-pro';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';
import { BookingsTableRow, bookingToBookingsTableRow } from './utils/rows';
import { getBookingsTableColumns } from './utils/columns';
import { push, Routes } from 'routing';
import { getSearchResults } from 'common/utils/searchFunctions';

import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';

interface BaseBookingsTableProps {
	bookings: OrderInfo[];
	primaryDateColumn: 'created' | 'completed';
	searchValue?: string;
	loading?: boolean;
	Toolbar?: GridSlotsComponent['Toolbar'];
	hiddenColumns?: Array<keyof BookingsTableRow>;
}

const BaseBookingsTable = (props: BaseBookingsTableProps) => {
	const { t } = useTranslation();
	const { bookings, searchValue, loading, hiddenColumns, primaryDateColumn, Toolbar } = props;

	const lang = useSelector(LanguageSelectors.language);
	const currency = useSelector(ShopSelectors.getShopCurrency);
	const dateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const deliveryOptions = useSelector(ShopSelectors.shopDeliveryOptionsById);
	const stockProductsById = useSelector(StockSelectors.stockProductsById);

	const apiRef = useGridApiRef();

	const columns = useMemo(() => {
		return getBookingsTableColumns({
			t,
			lang,
			currency,
			dateFormat,
			hiddenColumns,
			deliveryOptions,
			primaryDateColumn,
		});
	}, [t, lang, currency, dateFormat, hiddenColumns, deliveryOptions, primaryDateColumn]);

	const filteredBookings = useMemo(() => {
		return !!searchValue
			? getSearchResults(searchValue, bookings, ['NAME', 'PRODUCT_CODE', 'ORDER_BARCODE'])
			: bookings;
	}, [bookings, searchValue]);

	const rows = useMemo(() => {
		const mapFunction = bookingToBookingsTableRow({ t, lang, stockProductsById });

		return filteredBookings.map(mapFunction);
	}, [t, lang, filteredBookings, stockProductsById]);

	return (
		<ContainerBox padding="none">
			<RentleDataGrid
				apiRef={apiRef}
				rows={rows}
				columns={columns}
				density="comfortable"
				rowHeight={80}
				autoHeight
				components={{
					Toolbar,
				}}
				onRowClick={(params) => push(Routes.BookingPage(params.id as string))}
				loading={loading}
				componentsProps={{
					noRowsOverlay: {
						title: !!searchValue
							? t('orders.noMatchingBookingsFound', {
									searchValue,
									defaultValue: 'No matching orders found for "{{searchValue}}"',
							  })
							: t('orders.noOrders', 'No orders'),
					},
				}}
			/>
		</ContainerBox>
	);
};

export default BaseBookingsTable;
